import * as amplitude from '@amplitude/analytics-browser'
import { PluginType, Result, Event, Plugin } from '@amplitude/analytics-types'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { SessionReplayOptions } from '@amplitude/plugin-session-replay-browser/lib/esm/typings/session-replay'
import {
  AmplitudeUserProperties,
  AnalyticsCallbacks,
  InitializeAmplitudeOptions,
  Settings,
  TrackedEvent,
} from './types'

export type { Plugin, Result, Event, PluginType }

const SESSION_TIMEOUT = 1000 * 60 * 30 // 30 min
const ANALYTICS_LOCAL_STORAGE_KEY = 'analytics_local_settings'
const START_SESSION_EVENT = 'system/start-session'
const END_SESSION_EVENT = 'system/end-session'
const MAX_RANDOM = 99999
let appPrefix = ''
let eventSource = ''
let sessionTimeout: number = SESSION_TIMEOUT
let sessionTimeoutID = -1

const analyticsCallbacks: AnalyticsCallbacks = {}

const analyticsLocalSettings = initSettings()

function generateSessionID() {
  const randomID = Date.now()
  const customID = Math.floor(randomID + Math.random() * MAX_RANDOM)

  return customID
}

function initSettings(): Settings {
  const { currentSessionID, timestampLastFiredEvent, isActiveSession } =
    JSON.parse(
      localStorage.getItem(ANALYTICS_LOCAL_STORAGE_KEY) || '{}'
    ) as Partial<Settings>

  return {
    timestampLastFiredEvent: timestampLastFiredEvent || 0,
    currentSessionID: currentSessionID || generateSessionID(),
    isActiveSession: isActiveSession || false,
  }
}

function saveLocalSettings() {
  localStorage.setItem(
    ANALYTICS_LOCAL_STORAGE_KEY,
    JSON.stringify(analyticsLocalSettings)
  )
}

function getCurrentSessionID() {
  return analyticsLocalSettings.currentSessionID
}

function resetSessionID() {
  const newSessionId = generateSessionID()

  amplitude.setSessionId(newSessionId)
  analyticsLocalSettings.currentSessionID = newSessionId
  saveLocalSettings()
}

function isActiveSession() {
  return analyticsLocalSettings.isActiveSession
}

function setActiveSession(value: boolean) {
  analyticsLocalSettings.isActiveSession = value
  saveLocalSettings()
}

function closeCurrentSession() {
  if (!amplitude.getUserId()) {
    return
  }
  amplitude.track(`${appPrefix}/${END_SESSION_EVENT}`, { source: eventSource })
  amplitude.setTransport('beacon') // https://www.docs.developers.amplitude.com/data/sdks/typescript-browser/#use-sendbeacon
  amplitude.flush().promise.then(() => {
    amplitude.setTransport('fetch')
  })
  setActiveSession(false)
}

function startSession(onLogin: boolean) {
  if (onLogin) {
    resetSessionID()
    if (amplitude.getUserId()) {
      amplitude.track(`${appPrefix}/${START_SESSION_EVENT}`, {
        source: eventSource,
      })
      amplitude.setTransport('beacon')
      amplitude.flush().promise.then(() => {
        amplitude.setTransport('fetch')
      })
    }
    setActiveSession(true)
  }
}

function setEndSessionTimeout(): void {
  clearTimeout(sessionTimeoutID)
  sessionTimeoutID = window.setTimeout(closeCurrentSession, sessionTimeout)
}

function setTimestampLastFiredEvent(value: number) {
  analyticsLocalSettings.timestampLastFiredEvent = value
  saveLocalSettings()
  setEndSessionTimeout()
}

function getTimestampLastFiredEvent() {
  return analyticsLocalSettings.timestampLastFiredEvent
}

function isStaleSession() {
  const currentTime = new Date().getTime()
  return currentTime - sessionTimeout >= getTimestampLastFiredEvent()
}

export function init({
  token,
  appName,
  callbacks,
  amplitudeConfig,
  source,
}: InitializeAmplitudeOptions): boolean {
  appPrefix = appName
  eventSource = source || appName
  sessionTimeout = amplitudeConfig?.sessionTimeout || SESSION_TIMEOUT

  const config = {
    ...amplitudeConfig,
    // add 1 second "threshold" to Amplitude's sessionTimeout,
    // to avoid race condition between our custom session expiration timer and Amplitude's internal timer
    sessionTimeout: sessionTimeout + 1000,
  }

  if (callbacks) {
    Object.assign(analyticsCallbacks, callbacks)
  }
  try {
    amplitude.init(token, {
      autocapture: false,
      ...config,
    })
    amplitude.setOptOut(false)
    amplitude.setSessionId(getCurrentSessionID())
    return true
  } catch (error) {
    return false
  }
}

export function addPlugin(plugin: Plugin) {
  amplitude.add(plugin)
}

export function removePlugin(name: string) {
  amplitude.remove(name)
}

export function endSession() {
  closeCurrentSession()
  /**
   * In order to also reset device id, use amplitude.reset()
   * https://www.docs.developers.amplitude.com/data/sdks/browser-2/#reset-when-user-logs-out
   */
  amplitude.setUserId(undefined)
  resetSessionID()

  if (analyticsCallbacks.onSessionEnd) {
    analyticsCallbacks.onSessionEnd()
  }
}

export function identifyUser(
  user: AmplitudeUserProperties,
  onLogin: boolean,
  associatedGroup?: { groupType: string; groupValue: string },
  deprecatedUserProps?: string[]
) {
  amplitude.setUserId(user.userId)

  const userProperties = new amplitude.Identify()
  Object.entries(user).forEach(([key, value]) => {
    userProperties.set(key, value)
  })

  if (deprecatedUserProps) {
    deprecatedUserProps.forEach((prop) => {
      userProperties.unset(prop)
    })
  }
  /**
   * Amplitude custom user properties:
   * https://help.amplitude.com/hc/en-us/articles/115002380567-Overview-of-user-properties-and-event-properties-in-Amplitude#h_39e46c92-7b7f-4358-a96f-c82cc3342e3e
   */
  amplitude.identify(userProperties)

  /**
   * End previous session if it wasn't properly closed
   * (e.g. user closed browser tab before session timeout expired)
   */
  if (isActiveSession() && isStaleSession()) {
    // ensure that end session event will have the same session ID from previous events
    amplitude.setSessionId(getCurrentSessionID())
    closeCurrentSession()
  }

  startSession(onLogin)

  if (associatedGroup) {
    // Example: amplitude.setGroup('carrier id', '123456')
    amplitude.setGroup(associatedGroup.groupType, associatedGroup.groupValue)
  }
}

export function trackEvent(event: TrackedEvent) {
  if (isStaleSession()) {
    startSession(true)
  }
  amplitude.track(event.name, event.properties)
  setTimestampLastFiredEvent(new Date().valueOf())
}

export function stopTracking() {
  endSession()
  amplitude.setOptOut(true)
}

let sessionReplayInitialized = false

export function initSessionReplay(options: SessionReplayOptions) {
  const sessionReplay = sessionReplayPlugin({
    ...options,
    forceSessionTracking: false,
  })

  amplitude.add(sessionReplay)
  sessionReplayInitialized = true
}

export function stopSessionReplay() {
  if (sessionReplayInitialized) {
    amplitude.remove('@amplitude/plugin-session-replay-browser')
    sessionReplayInitialized = false
  }
}
